import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Backdrop,
  Button,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, OpenInNew as OpenInNewIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import geocodingService from '../services/geocodingService';
import { formatDate } from '../utils/dateUtils';
import axios from 'axios';
import { useSnackbar } from 'notistack';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Add this component to handle bounds fitting
const MapBoundsHandler = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => loc.coordinates));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);

  return null;
};

// Patagonia color scheme
const pieChartColors = [
  '#0468BF',  // Primary Blue
  '#04BF55',  // Primary Green
  '#F2D027',  // Primary Yellow
  '#F28705',  // Primary Orange
  '#D92B04',  // Primary Red
  '#0357A3',  // Darker Blue
  '#03A348',  // Darker Green
  '#D9BB22',  // Darker Yellow
  '#D97904',  // Darker Orange
  '#BF2604',  // Darker Red
];

const ClientDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = location.state?.client;
  const [locationData, setLocationData] = useState([]);
  const [isPlottingLocations, setIsPlottingLocations] = useState(true);
  
  // State for suggested locations - moved to top level
  const [suggestedLocations, setSuggestedLocations] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [selectedLocations, setSelectedLocations] = useState(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [savingRecommendedLocations, setSavingRecommendedLocations] = useState(false);
  
  // State for Salesforce recommended locations
  const [recommendedLocations, setRecommendedLocations] = useState([]);
  const [loadingRecommendedLocations, setLoadingRecommendedLocations] = useState(false);
  const [deletingLocationId, setDeletingLocationId] = useState(null);

  // WordPress API call to get matching properties
  const fetchSuggestedLocationsWithTermIds = async (designTermId, locationTermId, propertyTypeTermId) => {
    const wpBaseUrl = process.env.REACT_APP_WORDPRESS_URL || 'https://www.purelocations.com.au';
    
    try {
      // Build query with term IDs directly, sorted by newest first
      let apiUrl = `${wpBaseUrl}/wp-json/wp/v2/property?per_page=10&orderby=date&order=desc`;
      if (designTermId) {
        apiUrl += `&design=${designTermId}`;
      }
      if (locationTermId) {
        apiUrl += `&location=${locationTermId}`;
      }
      if (propertyTypeTermId) {
        apiUrl += `&property-type=${propertyTypeTermId}`;
      }
      
      console.log('Final property API URL with term IDs:', apiUrl);
      const response = await fetch(apiUrl);
      console.log('Properties response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('Properties API error response:', errorText);
        throw new Error('Failed to fetch properties');
      }
      
      const properties = await response.json();
      console.log('Properties received:', properties);
      
      // Process each property to get proper image URLs and location names
      const processedProperties = await Promise.all(properties.map(async (property) => {
        console.log(`Processing property: ${property.title.rendered}`);
        
        // Get featured media URL
        let imageUrl = '/placeholder-image.jpg';
        if (property.featured_media) {
          try {
            console.log('Getting featured media for ID:', property.featured_media);
            const mediaResponse = await fetch(`${wpBaseUrl}/wp-json/wp/v2/media/${property.featured_media}`);
            if (mediaResponse.ok) {
              const mediaData = await mediaResponse.json();
              imageUrl = mediaData.source_url || imageUrl;
              console.log('Featured media URL:', imageUrl);
            }
          } catch (error) {
            console.error('Error fetching featured media:', error);
          }
        }
        
        // Get location term names
        let locationNames = [];
        if (property.location && property.location.length > 0) {
          console.log('Getting location names for term IDs:', property.location);
          try {
            const locationTermPromises = property.location.map(async (termId) => {
              const termResponse = await fetch(`${wpBaseUrl}/wp-json/wp/v2/location/${termId}`);
              if (termResponse.ok) {
                const termData = await termResponse.json();
                return termData.name;
              }
              return null;
            });
            
            const termNames = await Promise.all(locationTermPromises);
            locationNames = termNames.filter(name => name !== null);
            console.log('Location names:', locationNames);
          } catch (error) {
            console.error('Error fetching location terms:', error);
          }
        }
        
        // Decode HTML entities and strip HTML tags
        const decodeHtmlEntities = (text) => {
          const textarea = document.createElement('textarea');
          textarea.innerHTML = text;
          return textarea.value;
        };

        return {
          id: property.id,
          title: property.title.rendered,
          location: locationNames.length > 0 ? locationNames.join(', ') : 'Location not specified',
          excerpt: decodeHtmlEntities(property.excerpt.rendered.replace(/<[^>]*>/g, '')), // Strip HTML and decode entities
          image: imageUrl,
          link: property.link,
          date: property.date || property.modified || null // Store date for sorting
        };
      }));
      
      console.log('Processed properties with images and locations:', processedProperties);
      return processedProperties;
    } catch (error) {
      console.error('Error fetching suggested locations with term IDs:', error);
      console.error('Full error details:', error.message);
      console.error('WordPress URL being used:', wpBaseUrl);
      return [];
    }
  };

  const fetchSuggestedLocations = async (designStyle, region) => {
    const wpBaseUrl = process.env.REACT_APP_WORDPRESS_URL || 'https://www.purelocations.com.au';
    
    try {
      // Step 1: Get design taxonomy term ID
      console.log('Looking up design term ID for:', designStyle);
      const designApiUrl = `${wpBaseUrl}/wp-json/wp/v2/design`;
      console.log('Design API call:', designApiUrl);
      const designResponse = await fetch(designApiUrl);
      console.log('Design response status:', designResponse.status);
      const designTerms = await designResponse.json();
      console.log('Design terms received:', designTerms);
      const designTerm = designTerms.find(term => term.name === designStyle);
      const designId = designTerm ? designTerm.id : null;
      console.log('Design term found:', designTerm);
      console.log('Design term ID found:', designId);

      // Step 2: Get location taxonomy term ID  
      console.log('Looking up location term ID for:', region);
      const locationApiUrl = `${wpBaseUrl}/wp-json/wp/v2/location`;
      console.log('Location API call:', locationApiUrl);
      const locationResponse = await fetch(locationApiUrl);
      console.log('Location response status:', locationResponse.status);
      const locationTerms = await locationResponse.json();
      console.log('Location terms received:', locationTerms);
      const locationTerm = locationTerms.find(term => term.name === region);
      const locationId = locationTerm ? locationTerm.id : null;
      console.log('Location term found:', locationTerm);
      console.log('Location term ID found:', locationId);

      // Step 3: Query properties with term IDs
      let apiUrl = `${wpBaseUrl}/wp-json/wp/v2/property?per_page=6`;
      if (designId) {
        apiUrl += `&design=${designId}`;
      }
      if (locationId) {
        apiUrl += `&location=${locationId}`;
      }
      
      console.log('Final property API URL:', apiUrl);
      const propertiesResponse = await fetch(apiUrl);
      console.log('Properties response status:', propertiesResponse.status);
      
      if (!propertiesResponse.ok) {
        const errorText = await propertiesResponse.text();
        console.log('Properties API error response:', errorText);
        throw new Error('Failed to fetch properties');
      }
      
      const properties = await propertiesResponse.json();
      console.log('Properties received:', properties);
      // Decode HTML entities and strip HTML tags
      const decodeHtmlEntities = (text) => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
      };

      return properties.map(property => ({
        id: property.id,
        title: property.title.rendered,
        location: property.location || property.meta?.location || 'Location not specified',
        excerpt: decodeHtmlEntities(property.excerpt.rendered.replace(/<[^>]*>/g, '')), // Strip HTML and decode entities
        image: property.featured_media_url || '/placeholder-image.jpg',
        link: property.link
      }));
    } catch (error) {
      console.error('Error fetching suggested locations:', error);
      console.error('Full error details:', error.message);
      console.error('WordPress URL being used:', wpBaseUrl);
      // Return mock data for development
      return [
        
      ];
    }
  };

  useEffect(() => {
    if (!client) return;

    const loadLocationData = async () => {
      setIsPlottingLocations(true);
      const locations = new Map();
      
      // First pass: collect all locations
      client.opportunities?.forEach(opp => {
        if (!opp.Location_Name__r?.Address__c) return;
        
        const locationKey = opp.Location_Name__r.Name;
        if (!locations.has(locationKey)) {
          locations.set(locationKey, {
            name: locationKey,
            address: opp.Location_Name__r.Address__c,
            bookings: [],
            coordinates: null
          });
        }
        
        locations.get(locationKey).bookings.push({
          date: formatDate(opp.CloseDate),
          amount: Number(opp.Total_Hire_Fees__c) || 0,
          enquiryName: opp.Name
        });
      });

      // Second pass: geocode addresses
      const locationArray = Array.from(locations.values());
      const geocodedLocations = await Promise.all(
        locationArray.map(async (location) => {
          try {
            const coordinates = await geocodingService.geocodeAddress(location.address);
            return {
              ...location,
              coordinates: coordinates || [-33.8688, 151.2093] // Default to Sydney if geocoding fails
            };
          } catch (error) {
            console.error('Geocoding error:', error);
            return {
              ...location,
              coordinates: [-33.8688, 151.2093] // Default to Sydney
            };
          }
        })
      );

      // Remove the manual center/zoom calculation since we'll use bounds
      setLocationData(geocodedLocations);
      setIsPlottingLocations(false);
    };

    loadLocationData();
  }, [client]);

  // Load suggested locations - using the same logic as the main component
  useEffect(() => {
    if (!client) return;
    
    const loadSuggestions = async () => {
      console.log('=== GETTING RECENT BOOKINGS FOR METADATA LOOKUP ===');
      
      // Get last 5 closed won bookings
      const recentBookings = client.opportunities
        ?.filter(opp => opp.StageName === 'Closed Won' && opp.Location_Name__r?.Name)
        ?.sort((a, b) => new Date(b.CloseDate) - new Date(a.CloseDate))
        ?.slice(0, 5) || [];
      
      console.log('Recent bookings for lookup:', recentBookings.map(booking => ({
        name: booking.Name,
        locationName: booking.Location_Name__r?.Name,
        closeDate: booking.CloseDate
      })));

      if (recentBookings.length === 0) {
        console.log('No recent bookings found');
        setLoadingSuggestions(false);
        return;
      }

      // Look up each property in WordPress to get its taxonomy data
      const wpBaseUrl = process.env.REACT_APP_WORDPRESS_URL || 'https://www.purelocations.com.au';
      const propertyMetadata = [];
      
      for (const booking of recentBookings) {
        const propertyName = booking.Location_Name__r.Name;
        console.log(`Looking up WordPress property: "${propertyName}"`);
        
        try {
          // Search for property by title
          const searchUrl = `${wpBaseUrl}/wp-json/wp/v2/property?search=${encodeURIComponent(propertyName)}&per_page=1`;
          console.log('Property search URL:', searchUrl);
          
          const response = await fetch(searchUrl);
          console.log('Property search response status:', response.status);
          
          if (response.ok) {
            const properties = await response.json();
            console.log('Property search results:', properties);
            
            if (properties.length > 0) {
              const property = properties[0];
              console.log(`Found property: ${property.title.rendered} (ID: ${property.id})`);
              
              // Get the property with full taxonomy data
              const propertyUrl = `${wpBaseUrl}/wp-json/wp/v2/property/${property.id}`;
              console.log('Full property URL:', propertyUrl);
              
              const fullPropertyResponse = await fetch(propertyUrl);
              if (fullPropertyResponse.ok) {
                const fullProperty = await fullPropertyResponse.json();
                console.log('Full property data:', fullProperty);
                
                propertyMetadata.push({
                  bookingName: booking.Name,
                  propertyTitle: fullProperty.title.rendered,
                  designTerms: fullProperty.design || [],
                  locationTerms: fullProperty.location || [],
                  propertyTypeTerms: fullProperty['property-type'] || []
                });
              }
            } else {
              console.log(`No WordPress property found for: "${propertyName}"`);
            }
          }
        } catch (error) {
          console.error(`Error looking up property "${propertyName}":`, error);
        }
      }
      
      console.log('=== PROPERTY METADATA COLLECTED ===');
      console.log('All property metadata:', propertyMetadata);
      
             // Analyze the taxonomy data to find most common terms across all categories
       const designTermCounts = {};
       const locationTermCounts = {};
       const propertyTypeTermCounts = {};
       
       propertyMetadata.forEach(property => {
         console.log(`Analyzing property: ${property.propertyTitle}`);
         console.log('- Design terms:', property.designTerms);
         console.log('- Location terms:', property.locationTerms);
         console.log('- Property type terms:', property.propertyTypeTerms);
         
         property.designTerms.forEach(termId => {
           designTermCounts[termId] = (designTermCounts[termId] || 0) + 1;
         });
         property.locationTerms.forEach(termId => {
           locationTermCounts[termId] = (locationTermCounts[termId] || 0) + 1;
         });
         property.propertyTypeTerms.forEach(termId => {
           propertyTypeTermCounts[termId] = (propertyTypeTermCounts[termId] || 0) + 1;
         });
       });
       
       console.log('Design term counts:', designTermCounts);
       console.log('Location term counts:', locationTermCounts);
       console.log('Property type term counts:', propertyTypeTermCounts);
       
       // Get the most common terms from each category, but for locations prefer more specific terms
       const getMostCommonTermIds = (termCounts, maxTerms = 2) => {
         return Object.entries(termCounts)
           .sort(([,a], [,b]) => b - a)
           .slice(0, maxTerms)
           .map(([termId, count]) => ({ termId: parseInt(termId), count }));
       };
       
       const topDesignTerms = getMostCommonTermIds(designTermCounts, 2);
       const topLocationTerms = getMostCommonTermIds(locationTermCounts, 3); // Get top 3 for better selection
       const topPropertyTypeTerms = getMostCommonTermIds(propertyTypeTermCounts, 2);
       
       console.log('Top design terms:', topDesignTerms);
       console.log('Top location terms:', topLocationTerms);
       console.log('Top property type terms:', topPropertyTypeTerms);
       
       // For location, try to find a mid-level term (like Sydney) instead of top-level (NSW)
       let bestLocationTermId = null;
       for (const locationTerm of topLocationTerms) {
         console.log(`Checking location term ID ${locationTerm.termId} for specificity...`);
         try {
           const termResponse = await fetch(`${wpBaseUrl}/wp-json/wp/v2/location/${locationTerm.termId}`);
           if (termResponse.ok) {
             const termData = await termResponse.json();
             console.log(`Term ${locationTerm.termId} data:`, { name: termData.name, parent: termData.parent });
             
             // Prefer terms that have a parent (not top-level) but aren't too specific
             if (termData.parent && termData.parent !== 0) {
               bestLocationTermId = locationTerm.termId;
               console.log(`Selected term ${locationTerm.termId} (${termData.name}) as it has parent ${termData.parent}`);
               break;
             }
           }
         } catch (error) {
           console.error(`Error checking term ${locationTerm.termId}:`, error);
         }
       }
       
       // Fallback to most common if no suitable mid-level term found
       if (!bestLocationTermId && topLocationTerms.length > 0) {
         bestLocationTermId = topLocationTerms[0].termId;
         console.log(`Fallback to most common location term: ${bestLocationTermId}`);
       }
       
       // Use the most common term from each category
       const mostCommonDesignId = topDesignTerms.length > 0 ? topDesignTerms[0].termId : null;
       const mostCommonLocationId = bestLocationTermId;
       const mostCommonPropertyTypeId = topPropertyTypeTerms.length > 0 ? topPropertyTypeTerms[0].termId : null;
       
       console.log('Selected design term ID:', mostCommonDesignId);
       console.log('Selected location term ID:', mostCommonLocationId);
       console.log('Selected property type term ID:', mostCommonPropertyTypeId);
      
             if (mostCommonDesignId || mostCommonLocationId || mostCommonPropertyTypeId) {
         setLoadingSuggestions(true);
         
         // Look up the actual term names for display in UI
         let designTermName = 'Not specified';
         let locationTermName = 'Not specified';
         
         if (mostCommonDesignId) {
           try {
             const designTermResponse = await fetch(`${wpBaseUrl}/wp-json/wp/v2/design/${mostCommonDesignId}`);
             if (designTermResponse.ok) {
               const designTermData = await designTermResponse.json();
               designTermName = designTermData.name;
               console.log('Design term name:', designTermName);
             }
           } catch (error) {
             console.error('Error fetching design term name:', error);
           }
         }
         
         if (mostCommonLocationId) {
           try {
             console.log('Looking up location term hierarchy for ID:', mostCommonLocationId);
             const locationTermResponse = await fetch(`${wpBaseUrl}/wp-json/wp/v2/location/${mostCommonLocationId}`);
             if (locationTermResponse.ok) {
               const locationTermData = await locationTermResponse.json();
               console.log('Location term data:', locationTermData);
               
               // Traverse up the hierarchy to find the 2nd level (city/region)
               let currentTerm = locationTermData;
               let hierarchy = [currentTerm];
               
               // Build the complete hierarchy by following parent relationships
               while (currentTerm.parent && currentTerm.parent !== 0) {
                 console.log(`Getting parent term for ID: ${currentTerm.parent}`);
                 const parentResponse = await fetch(`${wpBaseUrl}/wp-json/wp/v2/location/${currentTerm.parent}`);
                 if (parentResponse.ok) {
                   const parentTerm = await parentResponse.json();
                   console.log('Parent term:', parentTerm);
                   hierarchy.unshift(parentTerm); // Add to beginning
                   currentTerm = parentTerm;
                 } else {
                   break;
                 }
               }
               
               console.log('Complete location hierarchy:', hierarchy.map(term => `${term.name} (ID: ${term.id})`));
               
               // Use the 2nd level in hierarchy (index 1) if available, otherwise use the first level
               if (hierarchy.length >= 2) {
                 locationTermName = hierarchy[1].name; // 2nd level (e.g., "Sydney")
                 console.log('Using 2nd level region:', locationTermName);
               } else if (hierarchy.length >= 1) {
                 locationTermName = hierarchy[0].name; // Fallback to 1st level
                 console.log('Using 1st level region (fallback):', locationTermName);
               } else {
                 locationTermName = locationTermData.name; // Ultimate fallback
                 console.log('Using original term name (fallback):', locationTermName);
               }
             }
           } catch (error) {
             console.error('Error fetching location term hierarchy:', error);
           }
         }
         
         // Store the term names for use in UI
         setSuggestedLocations([]);
         setLoadingSuggestions(true);
         
         const suggestions = await fetchSuggestedLocationsWithTermIds(mostCommonDesignId, mostCommonLocationId, mostCommonPropertyTypeId);
         
         // Get list of locations the client has already shot at (Closed Won opportunities only)
         const visitedLocationNames = new Set();
         client.opportunities?.forEach(opp => {
           // Only include Closed Won opportunities as these represent actual shoots
           if (opp.StageName === 'Closed Won' && opp.Location_Name__r?.Name) {
             const locationName = opp.Location_Name__r.Name.toLowerCase().trim();
             visitedLocationNames.add(locationName);
             // Also add variations for better matching (remove common suffixes/prefixes)
             const normalizedName = locationName
               .replace(/\s+/g, ' ') // Normalize whitespace
               .replace(/^the\s+/i, '') // Remove leading "the"
               .trim();
             if (normalizedName !== locationName) {
               visitedLocationNames.add(normalizedName);
             }
           }
         });
         
         console.log('Locations client has already shot at (to filter out):', Array.from(visitedLocationNames));
         
        // Filter out properties that match visited locations
        const filteredSuggestions = suggestions.filter(property => {
          const propertyTitle = property.title.toLowerCase().trim();
          const normalizedPropertyTitle = propertyTitle
            .replace(/\s+/g, ' ') // Normalize whitespace
            .replace(/^the\s+/i, '') // Remove leading "the"
            .trim();
          
          // Check for exact match or normalized match
          const isAlreadyVisited = visitedLocationNames.has(propertyTitle) || 
                                   visitedLocationNames.has(normalizedPropertyTitle) ||
                                   // Also check if property title contains visited location name
                                   Array.from(visitedLocationNames).some(visited => 
                                     propertyTitle.includes(visited) || visited.includes(propertyTitle)
                                   );
          
          if (isAlreadyVisited) {
            console.log(`Filtering out already shot location: "${property.title}" (matches visited location)`);
          }
          return !isAlreadyVisited;
        });
        
        console.log(`Filtered suggestions: ${filteredSuggestions.length} of ${suggestions.length} properties remaining`);
        
        // Sort by date (newest first) - ensure consistent ordering even after filtering
        const sortedSuggestions = [...filteredSuggestions].sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        });
        
        // Add the term names to the suggestions data for UI display
        // Note: We'll filter out recommended locations in the UI component
        const suggestionsWithTermNames = {
          properties: sortedSuggestions,
          designTermName,
          locationTermName
        };
         
         setSuggestedLocations(suggestionsWithTermNames);
         setLoadingSuggestions(false);
       } else {
         setLoadingSuggestions(false);
       }
    };

    loadSuggestions();
  }, [client]);

  // Load recommended locations from Salesforce
  useEffect(() => {
    if (!client?.accountId) return;

    const loadRecommendedLocations = async () => {
      setLoadingRecommendedLocations(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bdm.purelocations.com.au/api/sf';
      
      try {
        // Query Recommended_Location__c records for this client
        const query = `SELECT Id, Name, Location_Account__c, Location_Account__r.Name, Location_Account__r.Address__c, CreatedDate FROM Recommended_Location__c WHERE Client_Account__c = '${client.accountId}' ORDER BY CreatedDate DESC`;
        
        const response = await axios.post(`${API_BASE_URL}/query`, { query });
        
        if (response.data?.records) {
          // Check which locations the client has already shot at
          const shotLocationNames = new Set();
          client.opportunities?.forEach(opp => {
            if (opp.StageName === 'Closed Won' && opp.Location_Name__r?.Name) {
              const locationName = opp.Location_Name__r.Name.toLowerCase().trim();
              shotLocationNames.add(locationName);
              // Also add normalized version
              const normalizedName = locationName
                .replace(/\s+/g, ' ')
                .replace(/^the\s+/i, '')
                .trim();
              if (normalizedName !== locationName) {
                shotLocationNames.add(normalizedName);
              }
            }
          });

          const recommended = response.data.records.map(rec => {
            const locationName = (rec.Location_Account__r?.Name || 'Unknown Location').toLowerCase().trim();
            const normalizedLocationName = locationName
              .replace(/\s+/g, ' ')
              .replace(/^the\s+/i, '')
              .trim();
            
            const hasShot = shotLocationNames.has(locationName) || 
                           shotLocationNames.has(normalizedLocationName) ||
                           Array.from(shotLocationNames).some(shotName => 
                             locationName.includes(shotName) || shotName.includes(locationName)
                           );

            return {
              id: rec.Id,
              name: rec.Location_Account__r?.Name || 'Unknown Location',
              locationAccountId: rec.Location_Account__c,
              address: rec.Location_Account__r?.Address__c || '',
              createdDate: rec.CreatedDate,
              hasShot: hasShot
            };
          });
          setRecommendedLocations(recommended);
        } else {
          setRecommendedLocations([]);
        }
      } catch (error) {
        console.error('Error loading recommended locations:', error);
        setRecommendedLocations([]);
      } finally {
        setLoadingRecommendedLocations(false);
      }
    };

    loadRecommendedLocations();
  }, [client?.accountId, client?.opportunities]);

  // Filter suggested locations to exclude those already recommended
  const getFilteredSuggestedLocations = () => {
    if (!suggestedLocations.properties || recommendedLocations.length === 0) {
      return suggestedLocations.properties || [];
    }

    // Create a set of recommended location names (normalized for matching)
    const recommendedLocationNames = new Set();
    recommendedLocations.forEach(rec => {
      const name = rec.name.toLowerCase().trim();
      recommendedLocationNames.add(name);
      // Also add normalized version
      const normalizedName = name
        .replace(/\s+/g, ' ')
        .replace(/^the\s+/i, '')
        .trim();
      if (normalizedName !== name) {
        recommendedLocationNames.add(normalizedName);
      }
    });

    // Filter out properties that match recommended locations
    return suggestedLocations.properties.filter(property => {
      const propertyTitle = property.title.toLowerCase().trim();
      const normalizedPropertyTitle = propertyTitle
        .replace(/\s+/g, ' ')
        .replace(/^the\s+/i, '')
        .trim();
      
      const isAlreadyRecommended = recommendedLocationNames.has(propertyTitle) || 
                                   recommendedLocationNames.has(normalizedPropertyTitle) ||
                                   Array.from(recommendedLocationNames).some(recName => 
                                     propertyTitle.includes(recName) || recName.includes(propertyTitle)
                                   );
      
      return !isAlreadyRecommended;
    });
  };

  // Delete recommended location from Salesforce
  const deleteRecommendedLocation = async (locationId) => {
    if (!locationId || !window.confirm('Are you sure you want to remove this recommended location?')) {
      return;
    }

    setDeletingLocationId(locationId);
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bdm.purelocations.com.au/api/sf';

    try {
      const response = await axios.delete(`${API_BASE_URL}/deleteRecommendedLocation/${locationId}`);
      
      if (response.data.success) {
        // Remove from local state
        setRecommendedLocations(prev => prev.filter(loc => loc.id !== locationId));
        setSnackbarMessage('Recommended location removed successfully');
        setSnackbarOpen(true);
      } else {
        throw new Error('Failed to delete location');
      }
    } catch (error) {
      console.error('Error deleting recommended location:', error);
      setSnackbarMessage(`Error removing location: ${error.response?.data?.error || error.message}`);
      setSnackbarOpen(true);
    } finally {
      setDeletingLocationId(null);
    }
  };

  // Handle checkbox selection
  const handleLocationSelect = (locationId, isChecked) => {
    const newSelected = new Set(selectedLocations);
    if (isChecked) {
      newSelected.add(locationId);
    } else {
      newSelected.delete(locationId);
    }
    setSelectedLocations(newSelected);
  };

  // Handle select all locations
  const handleSelectAll = (isChecked) => {
    const filteredSuggestions = getFilteredSuggestedLocations();
    if (isChecked && filteredSuggestions && filteredSuggestions.length > 0) {
      const allIds = new Set(filteredSuggestions.map(location => location.id));
      setSelectedLocations(allIds);
    } else {
      setSelectedLocations(new Set());
    }
  };

  // Save selected locations to Salesforce as Recommended_Location__c records
  const saveRecommendedLocations = async () => {
    if (!client?.accountId || selectedLocations.size === 0) {
      setSnackbarMessage('No locations selected or client account not found');
      setSnackbarOpen(true);
      return;
    }

    const filteredSuggestions = getFilteredSuggestedLocations();
    const selectedProperties = filteredSuggestions.filter(
      location => selectedLocations.has(location.id)
    );

    if (selectedProperties.length === 0) {
      setSnackbarMessage('No valid locations selected');
      setSnackbarOpen(true);
      return;
    }

    setSavingRecommendedLocations(true);
    setSnackbarMessage('Saving recommended locations...');
    setSnackbarOpen(true);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bdm.purelocations.com.au/api/sf';
    const results = [];
    const errors = [];

    try {
      // Look up Salesforce Account IDs for each location by name
      for (const property of selectedProperties) {
        try {
          // Query Salesforce to find the Account by name (Location Account RecordTypeId)
          const locationName = property.title.replace(/'/g, "''"); // Escape single quotes for SOQL
          const query = `SELECT Id, Name FROM Account WHERE Name = '${locationName}' AND RecordTypeId = '0125g0000004aLfAAI' LIMIT 1`;
          
          const queryResponse = await axios.post(`${API_BASE_URL}/query`, { query });
          
          if (queryResponse.data?.records && queryResponse.data.records.length > 0) {
            const locationAccountId = queryResponse.data.records[0].Id;
            
            // Create Recommended_Location__c record
            const createResponse = await axios.post(
              `${API_BASE_URL}/createRecommendedLocation`,
              {
                Client_Account__c: client.accountId,
                Location_Account__c: locationAccountId
              }
            );

            if (createResponse.data.success) {
              results.push({
                property: property.title,
                success: true,
                id: createResponse.data.id,
                alreadyExists: createResponse.data.alreadyExists || false
              });
            } else {
              errors.push({
                property: property.title,
                error: 'Failed to create record'
              });
            }
          } else {
            errors.push({
              property: property.title,
              error: 'Location not found in Salesforce'
            });
          }
        } catch (error) {
          console.error(`Error saving location ${property.title}:`, error);
          errors.push({
            property: property.title,
            error: error.response?.data?.error || error.message || 'Unknown error'
          });
        }
      }

      // Show results
      const successCount = results.filter(r => r.success).length;
      const alreadyExistsCount = results.filter(r => r.alreadyExists).length;
      const errorCount = errors.length;

      let message = '';
      if (errorCount === 0) {
        if (alreadyExistsCount > 0) {
          message = `Successfully saved ${successCount - alreadyExistsCount} new location(s). ${alreadyExistsCount} location(s) were already recommended.`;
        } else {
          message = `Successfully saved ${successCount} location(s) as recommended locations.`;
        }
      } else if (successCount > 0) {
        message = `Saved ${successCount} location(s). ${errorCount} location(s) failed: ${errors.map(e => e.property).join(', ')}`;
      } else {
        message = `Failed to save locations: ${errors.map(e => `${e.property} (${e.error})`).join('; ')}`;
      }

      setSnackbarMessage(message);
      setSnackbarOpen(true);

      // Clear selection after successful save
      if (errorCount === 0) {
        setSelectedLocations(new Set());
        // Reload recommended locations to update the list
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bdm.purelocations.com.au/api/sf';
        try {
          const query = `SELECT Id, Name, Location_Account__c, Location_Account__r.Name, Location_Account__r.Address__c, CreatedDate FROM Recommended_Location__c WHERE Client_Account__c = '${client.accountId}' ORDER BY CreatedDate DESC`;
          const response = await axios.post(`${API_BASE_URL}/query`, { query });
          if (response.data?.records) {
            // Check which locations the client has already shot at
            const shotLocationNames = new Set();
            client.opportunities?.forEach(opp => {
              if (opp.StageName === 'Closed Won' && opp.Location_Name__r?.Name) {
                const locationName = opp.Location_Name__r.Name.toLowerCase().trim();
                shotLocationNames.add(locationName);
                // Also add normalized version
                const normalizedName = locationName
                  .replace(/\s+/g, ' ')
                  .replace(/^the\s+/i, '')
                  .trim();
                if (normalizedName !== locationName) {
                  shotLocationNames.add(normalizedName);
                }
              }
            });

            const recommended = response.data.records.map(rec => {
              const locationName = (rec.Location_Account__r?.Name || 'Unknown Location').toLowerCase().trim();
              const normalizedLocationName = locationName
                .replace(/\s+/g, ' ')
                .replace(/^the\s+/i, '')
                .trim();
              
              const hasShot = shotLocationNames.has(locationName) || 
                             shotLocationNames.has(normalizedLocationName) ||
                             Array.from(shotLocationNames).some(shotName => 
                               locationName.includes(shotName) || shotName.includes(locationName)
                             );

              return {
                id: rec.Id,
                name: rec.Location_Account__r?.Name || 'Unknown Location',
                locationAccountId: rec.Location_Account__c,
                address: rec.Location_Account__r?.Address__c || '',
                createdDate: rec.CreatedDate,
                hasShot: hasShot
              };
            });
            setRecommendedLocations(recommended);
          }
        } catch (reloadError) {
          console.error('Error reloading recommended locations:', reloadError);
        }
      }
    } catch (error) {
      console.error('Error saving recommended locations:', error);
      setSnackbarMessage(`Error saving locations: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setSavingRecommendedLocations(false);
    }
  };

  // Generate HTML email content for selected locations
  const generateEmailHTML = () => {
    if (!suggestedLocations.properties || selectedLocations.size === 0) return '';

    const selectedProperties = suggestedLocations.properties.filter(
      location => selectedLocations.has(location.id)
    );

    const emailHTML = `
<table width="100%" cellpadding="0" cellspacing="0" style="font-family: Arial, sans-serif; color: #333; max-width: 800px; background-color: white;">
    ${selectedProperties.map(property => `
    <tr>
        <td style="border: 1px solid #e0e0e0; padding: 0; background-color: white;">
            <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse; background-color: white;">
                <tr>
                    <td style="width: 240px; vertical-align: top; padding: 0; background-color: white;">
                        <img src="${property.image}" alt="${property.title}" width="240" style="width: 240px !important; height: 120px; display: block;" />
                    </td>
                    <td style="vertical-align: top; padding: 15px; background-color: white;">
                        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: white;">
                            <tr>
                                <td style="background-color: white;">
                                    <h2 style="margin: 0 0 8px 0; font-size: 18px; font-weight: bold; color: #2c3e50;">${property.title}</h2>
                                </td>
                            </tr>
                            <tr>
                                <td style="background-color: white;">
                                    <p style="margin: 0 0 10px 0; font-size: 14px; color: #666;"><strong style="color: #333;">Location:</strong> ${property.location}</p>
                                </td>
                            </tr>
                            <tr>
                                <td style="background-color: white;">
                                    <p style="margin: 0 0 12px 0; font-size: 14px; line-height: 1.4; color: #555;">${property.excerpt}</p>
                                </td>
                            </tr>
                            <tr>
                                <td style="background-color: white;">
                                    <table cellpadding="0" cellspacing="0">
                                        <tr>
                                            <td style="background-color: #3498db; padding: 8px 16px; border-radius: 4px;">
                                                <a href="${property.link}" style="color: white; text-decoration: none; font-size: 13px; font-weight: bold;">View Full Details</a>
                                            </td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </table>
        </td>
    </tr>
    
    <!-- Spacer between properties -->
    <tr>
        <td style="height: 15px; line-height: 15px; background-color: white;">&nbsp;</td>
    </tr>
    `).join('')}
</table>`;

    return emailHTML;
  };

  // Copy email content to clipboard as rich text (for Outlook for Mac)
  const copyEmailToClipboard = async () => {
    const emailHTML = generateEmailHTML();
    
    if (!emailHTML) {
      setSnackbarMessage('Please select at least one location to copy');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      // Create a temporary div with the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = emailHTML;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      document.body.appendChild(tempDiv);
      
      // Select the content
      const range = document.createRange();
      range.selectNodeContents(tempDiv);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Copy the selected content (this will copy as rich text)
      const success = document.execCommand('copy');
      
      // Clean up
      selection.removeAllRanges();
      document.body.removeChild(tempDiv);
      
      if (success) {
        setSnackbarMessage(`Successfully copied ${selectedLocations.size} location(s) as formatted email content for Outlook`);
        setSnackbarOpen(true);
      } else {
        throw new Error('execCommand failed');
      }
    } catch (error) {
      console.error('Failed to copy formatted content:', error);
      try {
        // Fallback: copy as plain HTML text
        await navigator.clipboard.writeText(emailHTML);
        setSnackbarMessage(`Copied as HTML source code - paste into Outlook's HTML editor`);
        setSnackbarOpen(true);
      } catch (fallbackError) {
        // Final fallback
        const textArea = document.createElement('textarea');
        textArea.value = emailHTML;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setSnackbarMessage('Copied as HTML source - you may need to paste into HTML editor mode');
        setSnackbarOpen(true);
      }
    }
  };

  if (!client) {
    return <Typography>Client not found</Typography>;
  }

  // Transform opportunities into booking history rows
  const bookingRows = client.opportunities?.map(opp => ({
    id: opp.Id || Math.random().toString(),
    date: formatDate(opp.CloseDate),
    rawDate: new Date(opp.CloseDate), // Add rawDate for sorting
    enquiryName: opp.Name || 'N/A',
    brand: opp.Brand__c || 'N/A',
    designStyle: opp.Location_Name__r?.Design__c || 'Not Specified',
    location: opp.Location_Name__r?.Name || 'N/A',
    type: opp.Type || 'Shoot',
    amount: Number(opp.Total_Hire_Fees__c) || 0,
    grossProfit: Number(opp.Gross_Profit__c) || 0,
    status: opp.StageName || 'N/A'
  })) || [];

  // Let's also add some console.logs to debug the data
  // console.log('First opportunity:', client.opportunities?.[0]);
  // console.log('Location data:', client.opportunities?.[0]?.Location_Name__r);

  // Booking history columns
  const columns = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      sortable: true,
      sortComparator: (v1, v2, param1, param2) => {
        const date1 = param1?.row?.rawDate ? param1.row.rawDate.getTime() : 0;
        const date2 = param2?.row?.rawDate ? param2.row.rawDate.getTime() : 0;
        return date2 - date1;
      }
    },
    {
      field: 'enquiryName',
      headerName: 'Enquiry Name',
      flex: 2,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const status = params.value;
        let color;
        let variant = 'filled';
        
        switch (status) {
          case 'Closed Won':
            color = 'success';
            break;
          case 'Closed Lost':
            color = 'error';
            break;
          default:
            color = 'default';
        }
        
        return (
          <Chip
            label={status}
            color={color}
            variant={variant}
            size="small"
            sx={{
              minWidth: '90px',
              '& .MuiChip-label': {
                px: 1,
              }
            }}
          />
        );
      }
    },
    {
      field: 'brand',
      headerName: 'Brand',
      flex: 1,
    },
    {
      field: 'designStyle',
      headerName: 'Design Style',
      flex: 1,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
    },
   
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      type: 'number',
      valueFormatter: (params) => `$${Number(params).toLocaleString()}`,
    },
    {
      field: 'grossProfit',
      headerName: 'Gross Profit',
      flex: 1,
      type: 'number',
      valueFormatter: (params) => `$${Number(params).toLocaleString()}`,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: (params) => (
        <IconButton 
          size="small"
          onClick={() => {
            const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
            const oppId = client.opportunities.find(opp => opp.Name === params.row.enquiryName)?.Id;
            if (oppId) {
              window.open(`${sfBaseUrl}/lightning/r/Opportunity/${oppId}/view`, '_blank');
            }
          }}
          title="Open in Salesforce"
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Transform opportunities data for the chart
  const getChartData = () => {
    // Initialize array with all months
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    // Create initial data structure with all months
    const monthlyData = new Map(
      months.map(month => [month, {
        month,
        revenue: 0,
        bookings: 0
      }])
    );

    // Process opportunities
    client.opportunities?.forEach(opp => {
      if (!opp.CloseDate) return;

      const date = new Date(opp.CloseDate);
      const monthKey = months[date.getMonth()]; // Get month name
      
      const data = monthlyData.get(monthKey);
      data.revenue += Number(opp.Total_Hire_Fees__c) || 0;
      data.bookings += 1;
    });

    // Convert to array and ensure month order
    return months.map(month => monthlyData.get(month));
  };

  const chartData = {
    dataset: getChartData()
  };

  // Transform opportunities data for design styles chart
  const getDesignStylesData = () => {
    // Create a map to count design styles
    const designStyleCounts = new Map();
    let totalStyles = 0;

    client.opportunities?.forEach(opp => {
      const designString = opp.Location_Name__r?.Design__c || 'Not Specified';
      
      // Split the design string into individual styles
      const styles = designString.split(';').map(s => s.trim());
      
      // Count each individual style
      styles.forEach(style => {
        if (!designStyleCounts.has(style)) {
          designStyleCounts.set(style, 0);
        }
        designStyleCounts.set(style, designStyleCounts.get(style) + 1);
        totalStyles++;
      });
    });

    // Convert to array, calculate percentages, sort, and take top 10
    return Array.from(designStyleCounts.entries())
      .map(([label, count]) => ({
        label,
        count,
        value: (count / totalStyles) * 100
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)  // Take only top 10
      .map((style, index) => ({  // Add ids
        id: index,
        label: style.label,
        value: style.value,
        count: style.count
      }));
  };

  // Replace the static designStyles with dynamic data
  const designStyles = getDesignStylesData();

  // Get preferred month (month with most bookings)
  const getPreferredMonth = () => {
    const monthlyBookings = getChartData();
    return monthlyBookings.reduce((max, current) => 
      current.bookings > max.bookings ? current : max
    ).month;
  };

  // Get preferred style (style with highest percentage)
  const getPreferredStyle = () => {
    const styles = getDesignStylesData();
    return styles.length > 0 ? styles[0].label : 'Not Specified';  // First item is highest due to sort
  };

  // Get preferred region based on most frequent location
  const getPreferredRegion = () => {
    const regionCounts = new Map();
    
    client.opportunities?.forEach(opp => {
      if (opp.Location_Name__r?.Name && opp.StageName === 'Closed Won') {
        const location = opp.Location_Name__r.Name;
        regionCounts.set(location, (regionCounts.get(location) || 0) + 1);
      }
    });

    if (regionCounts.size === 0) return 'Sydney';
    
    // Get the most frequent location
    const mostFrequentLocation = Array.from(regionCounts.entries())
      .sort((a, b) => b[1] - a[1])[0][0];
    
    // Extract region/city from location name (simple approach)
    const region = mostFrequentLocation.split(',')[0].trim();
    return region || 'Sydney';
  };

  const preferredMonth = getPreferredMonth();
  const preferredStyle = getPreferredStyle();
  const preferredRegion = getPreferredRegion();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/clients')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flex: 1 }}>{client.clientName}</Typography>
          <IconButton 
            onClick={() => {
              const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
              window.open(`${sfBaseUrl}/lightning/r/Account/${client.accountId}/view`, '_blank');
            }}
            title="Open in Salesforce"
          >
            <OpenInNewIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={client.owner?.photoUrl}>
              {client.owner?.name?.charAt(0) || 'N/A'}
            </Avatar>
            <Box>
              <Typography variant="body2" color="text.secondary">Account Owner</Typography>
              <Typography>{client.owner?.name}</Typography>
            </Box>
          </Box>
          <Chip 
            label={`Preferred Month: ${preferredMonth}`} 
            sx={{ backgroundColor: '#0468BF', color: 'white' }} 
            variant="solid" 
          />
          <Chip 
            label={`Preferred Style: ${preferredStyle}`} 
            sx={{ backgroundColor: '#03A348', color: 'white' }} 
            variant="solid" 
          />
        </Box>
      </Box>

      {/* Content */}
      <Grid container spacing={3}>

{/* Revenue Chart - 50% width */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Lifetime Revenue and Bookings Trend</Typography>
              <Box sx={{ width: '100%', height: 400 }}>
                <LineChart
                  dataset={chartData.dataset}
                  margin={{ top: 20, right: 40, bottom: 80, left: 70 }}
                  grid={{ vertical: true, horizontal: true }}
                  xAxis={[{ 
                    dataKey: 'month',
                    scaleType: 'band',
                  }]}
                  yAxis={[
                    { 
                      id: 'revenue',
                      scaleType: 'linear',
                      valueFormatter: (value) => `$${value.toLocaleString()}`,
                      tickNumber: 6,
                      tickSize: 10,
                      labelStyle: {
                        fontSize: 12,
                        dx: -10,
                      },
                    },
                    { 
                      id: 'bookings',
                      scaleType: 'linear',
                      position: 'right',
                      tickNumber: 6,
                      tickSize: 10,
                    }
                  ]}
                  series={[
                    {
                      dataKey: 'revenue',
                      label: 'Monthly Revenue',
                      yAxisKey: 'revenue',
                      color: '#8884d8',
                      showMark: false,
                    },
                    {
                      dataKey: 'bookings',
                      label: 'Number of Bookings',
                      yAxisKey: 'bookings',
                      color: '#82ca9d',
                      showMark: false,
                    },
                  ]}
                  height={350}
                  slotProps={{
                    legend: {
                      direction: 'row',
                      position: { vertical: 'bottom', horizontal: 'middle' },
                      padding: { top: 20, bottom: 0, left: 0, right: 0 },
                      itemMarkWidth: 10,
                      itemMarkHeight: 10,
                      markGap: 5,
                      itemGap: 15,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Design Styles - 50% width */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Favourite Design Styles</Typography>
              <Box sx={{ width: '100%', height: 400 }}>
                <PieChart
                  series={[
                    {
                      data: designStyles,
                      highlightScope: { faded: 'global', highlighted: 'item' },
                      faded: { innerRadius: 10, additionalRadius: -10 },
                      innerRadius: 10,
                      paddingAngle: 0,
                      cornerRadius: 2,
                     
                    },
                  ]}
                  colors={pieChartColors}
                  height={350}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'middle', horizontal: 'right' },
                      padding: 20,
                      itemMarkWidth: 8,
                      itemMarkHeight: 8,
                      markGap: 5,
                      itemGap: 8,
                      labelStyle: {
                        fontSize: 12,
                      },
                    },
                  }}
                  margin={{ top: 20, right: 160, bottom: 50, left: 50 }}
                  tooltip={{
                    trigger: 'item',
                    formatter: (params) => `${params.label}: ${params.value.toFixed(1)}%`
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Location Suggestions and Recommended Locations - Side by side */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Suggested Locations - Left column */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', mb: 0.5 }}>
                    Suggested Locations Based on Your Preferences
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                    Style: {suggestedLocations.designTermName || 'Not specified'} • Region: {suggestedLocations.locationTermName || 'Not specified'}
                  </Typography>
                  
                  {loadingSuggestions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ ml: 2 }}>Loading suggestions...</Typography>
                    </Box>
                  ) : (() => {
                    const filteredSuggestions = getFilteredSuggestedLocations();
                    return filteredSuggestions && filteredSuggestions.length > 0 ? (
                      <Box>
                        {/* Select All and Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 0.75 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={filteredSuggestions && selectedLocations.size === filteredSuggestions.length && filteredSuggestions.length > 0}
                                indeterminate={selectedLocations.size > 0 && filteredSuggestions && selectedLocations.size < filteredSuggestions.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const allIds = new Set(filteredSuggestions.map(location => location.id));
                                    setSelectedLocations(allIds);
                                  } else {
                                    setSelectedLocations(new Set());
                                  }
                                }}
                                color="primary"
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Select All ({filteredSuggestions?.length || 0})</Typography>}
                            sx={{ m: 0 }}
                          />
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button 
                              variant="outlined" 
                              color="primary"
                              size="small"
                              disabled={selectedLocations.size === 0}
                              onClick={copyEmailToClipboard}
                              sx={{ fontSize: '0.75rem', px: 1, py: 0.5, height: 28 }}
                            >
                              Copy ({selectedLocations.size})
                            </Button>
                            <Button 
                              variant="contained" 
                              color="primary"
                              size="small"
                              disabled={selectedLocations.size === 0 || !client?.accountId || savingRecommendedLocations}
                              onClick={saveRecommendedLocations}
                              startIcon={savingRecommendedLocations ? <CircularProgress size={14} color="inherit" /> : null}
                              sx={{ fontSize: '0.75rem', px: 1, py: 0.5, height: 28 }}
                            >
                              {savingRecommendedLocations ? 'Saving...' : `Add to SF (${selectedLocations.size})`}
                            </Button>
                          </Box>
                        </Box>
                        
                        {/* Scrollable list container */}
                        <Box sx={{ maxHeight: 600, overflowY: 'auto', pr: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {filteredSuggestions.map((location) => (
                              <Card 
                                key={location.id} 
                                sx={{ 
                                  display: 'flex', 
                                  minHeight: 70,
                                  border: selectedLocations.has(location.id) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                  '&:hover': { borderColor: '#1976d2', boxShadow: 1 }
                                }}
                              >
                                {/* Checkbox */}
                                <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75 }}>
                                  <Checkbox
                                    checked={selectedLocations.has(location.id)}
                                    onChange={(e) => handleLocationSelect(location.id, e.target.checked)}
                                    color="primary"
                                    size="small"
                                    sx={{ p: 0.5 }}
                                  />
                                </Box>
                                
                                {/* Feature Image - Smaller */}
                                <Box
                                  sx={{
                                    width: 90,
                                    minWidth: 90,
                                    backgroundImage: `url(${location.image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundColor: '#f5f5f5',
                                    flexShrink: 0
                                  }}
                                />
                                
                                {/* Content - Compact */}
                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                                      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.875rem' }}>
                                        {location.title}
                                      </Typography>
                                      <Button 
                                        variant="outlined" 
                                        size="small"
                                        sx={{ minWidth: 'auto', px: 0.75, py: 0.25, fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                                        onClick={() => window.open(location.link, '_blank')}
                                      >
                                        View
                                      </Button>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem', lineHeight: 1.2 }}>
                                      {location.location || 'Location not specified'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.2, fontSize: '0.7rem' }}>
                                      {location.excerpt}
                                    </Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          {recommendedLocations.length > 0 
                            ? 'All suggested locations have been added as recommended locations.'
                            : 'No location suggestions available at this time.'}
                        </Typography>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>

            {/* Salesforce Recommended Locations - Right column */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', mb: 0.5 }}>
                    Salesforce Recommended Locations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                    {recommendedLocations.length} location{recommendedLocations.length !== 1 ? 's' : ''} recommended
                  </Typography>
                  
                  {loadingRecommendedLocations ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2 }}>Loading...</Typography>
                    </Box>
                  ) : recommendedLocations.length > 0 ? (
                    <Box sx={{ maxHeight: 600, overflowY: 'auto', pr: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {recommendedLocations.map((location) => (
                          <Card 
                            key={location.id} 
                            sx={{ 
                              display: 'flex', 
                              minHeight: 70,
                              border: '1px solid #e0e0e0',
                              backgroundColor: '#f9f9f9',
                              '&:hover': { borderColor: '#1976d2', boxShadow: 1 }
                            }}
                          >
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {location.name}
                                    </Typography>
                                    {location.hasShot && (
                                      <Chip 
                                        label="Shot" 
                                        size="small" 
                                        color="success" 
                                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
                                      />
                                    )}
                                  </Box>
                                  <Button 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ minWidth: 'auto', px: 0.75, py: 0.25, fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                                    startIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem' }} />}
                                    onClick={() => {
                                      const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
                                      window.open(`${sfBaseUrl}/lightning/r/Account/${location.locationAccountId}/view`, '_blank');
                                    }}
                                  >
                                    SF
                                  </Button>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {location.address && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {location.address}
                                      </Typography>
                                    )}
                                    {location.createdDate && (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        {new Date(location.createdDate).toLocaleDateString()}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    disabled={deletingLocationId === location.id}
                                    onClick={() => deleteRecommendedLocation(location.id)}
                                    startIcon={deletingLocationId === location.id ? (
                                      <CircularProgress size={12} color="inherit" />
                                    ) : (
                                      <DeleteIcon sx={{ fontSize: '0.75rem' }} />
                                    )}
                                    sx={{ minWidth: 'auto', px: 0.75, py: 0.25, fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                                  >
                                    {deletingLocationId === location.id ? 'Removing' : 'Remove'}
                                  </Button>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No recommended locations yet. Select locations from the suggestions list to add them.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Booking History - Full width */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Booking History</Typography>
              <Box sx={{ height: 400 }}>
                <DataGridPro
                  rows={bookingRows}
                  columns={columns}
                  density="comfortable"
                  pagination
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'date', sort: 'desc' }],
                    },
                    pagination: {
                      pageSize: 10,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        
        {/* Add Map Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Booking Locations</Typography>
              <Box sx={{ width: '100%', height: 400, position: 'relative' }}>
                <MapContainer 
                  center={[-33.8688, 151.2093]}  // Default center until bounds are calculated
                  zoom={4}  // Default zoom until bounds are calculated
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <MarkerClusterGroup
                    chunkedLoading
                    maxClusterRadius={50}
                  >
                    {locationData.map((location, index) => (
                      <Marker 
                        key={index} 
                        position={location.coordinates}
                      >
                        <Popup>
                          <Typography variant="subtitle2">{location.name}</Typography>
                          <Typography variant="body2">{location.address}</Typography>
                          <Typography variant="caption">
                            Bookings: {location.bookings.length}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            {location.bookings.map((booking, i) => (
                              <Typography key={i} variant="caption" display="block">
                                {booking.date}: ${booking.amount.toLocaleString()}
                              </Typography>
                            ))}
                          </Box>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                  <MapBoundsHandler locations={locationData} />
                </MapContainer>
                <Backdrop
                  open={isPlottingLocations}
                  sx={{
                    position: 'absolute',
                    color: '#fff',
                    zIndex: 1000,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CircularProgress color="inherit" />
                    <Typography>Plotting Locations...</Typography>
                  </Box>
                </Backdrop>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarMessage.includes('Failed') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientDetails; 